import { createApi } from '@reduxjs/toolkit/query/react';

import { authorizationApiUrl } from '@/api/utils/authorizationApiUrl';
import { isOnboardingUserInProgress } from '@/util/isOnNewUserPage';

import { axiosBaseQuery } from '../axios/axiosBaseQuery';
import { AddOrganizationDto } from './dtos/AddOrganizationDto';
import { EditOrganizationDto } from './dtos/EditOrganizationDto';
import { OrganizationsReply } from './dtos/OrganizationsReply';
import { Organization } from './models/Organization';
import { transformOrganizationsReply } from './utils/transformOrganizationsReply';

export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    organizations: builder.query<Organization[], void>({
      query: () => ({
        url: `${authorizationApiUrl()}/organizations/allowed/v2`,
      }),
      extraOptions: {
        // New users does not have access to any organizations yet
        // and thus this query will return HTTP 403, an error code.
        // Skip this query for new users to avoid getting the error.
        // There is also a bug in Keycloak if new users call this
        // endpoint before they accepting their invitation may cause
        // double entry in Keycloak.
        abort: isOnboardingUserInProgress(),
      },
      transformResponse: (dto: OrganizationsReply) => {
        return transformOrganizationsReply(dto);
      },
    }),

    addOrganization: builder.mutation<void, AddOrganizationDto>({
      query: (organization) => ({
        url: `${authorizationApiUrl()}/organizations`,
        method: 'PUT',
        data: organization,
      }),
    }),

    editOrganization: builder.mutation<void, EditOrganizationDto>({
      query: (organization) => ({
        url: `${authorizationApiUrl()}/organizations/${
          organization.organizationId
        }/name`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/text' },
        data: organization.organizationName,
      }),
    }),

    deleteOrganization: builder.mutation<void, string>({
      query: (organizationId) => ({
        url: `${authorizationApiUrl()}/organizations/${organizationId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useOrganizationsQuery,
  useAddOrganizationMutation,
  useEditOrganizationMutation,
  useDeleteOrganizationMutation,
  util,
} = organizationsApi;
